import { ReactElement, useState } from 'react'
import { Link } from 'react-router-dom'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@mui/material/styles'
import { heapEvent } from '@equipmentshare/heaper'
import { PickOrderSummaryFragment, usePickOrdersQuery } from '../../../graphql'
import { pickFlowTheme } from '../../../config/muiTheme'
import TableHeaderCell from '../components/TableHeaderCell'
import { useDcAdminContext } from '../contexts/DcAdminContext'
import { fulfillmentChain } from './chain'
import FulfillmentChainTable from './components/FulfillmentChainTable'
import { PICK_ORDER_PREFIX, PICK_ORDER_STATUS_CHIP_COLOR, PICK_ORDER_STATUS_LABEL } from './constants'
import { earliestNeededBy, formatDate, formatDateTime, runSize } from './summary'
import { userDisplayName } from './userDisplayName'

// Secondary columns step aside on a narrow screen rather than squeezing the rest.
const WIDE_ONLY = { display: { xs: 'none', md: 'table-cell' } }
const NO_WRAP = { whiteSpace: 'nowrap' }
// Read out, not shown: the column holds only the expand buttons.
const VISUALLY_HIDDEN = {
	position: 'absolute', width: '1px', height: '1px', m: '-1px', p: 0, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
} as const

/**
 * Who and where a run is going: the company or person on the first line, the
 * city and state beneath. An MTT loadout has no address, so it keeps the
 * description inventory-api renders for it.
 */
function Destination( { pickOrder }: { pickOrder: PickOrderSummaryFragment } ) {
	const address = pickOrder.shippingAddress
	const who = address?.companyName || address?.name
	if ( !address || !who ) {
		return <>{pickOrder.destinationDescription ?? '—'}</>
	}
	const where = [ address.city, address.state ].filter( Boolean ).join( ', ' )
	return (
		<>
			<Box>{who}</Box>
			{where && <Typography variant="body2" color="text.secondary">{where}</Typography>}
		</>
	)
}

const COLUMN_COUNT = 8

function PickOrderRow( { pickOrder }: { pickOrder: PickOrderSummaryFragment } ) {
	const [ open, setOpen ] = useState( false )
	const name = `${PICK_ORDER_PREFIX}${pickOrder.pickOrderNumber}`
	const chainId = `pick-order-chain-${pickOrder.id}`

	return (
		<>
			<TableRow hover sx={{ '& > td': { borderBottom: open ? 0 : undefined } }}>
				<TableCell padding="checkbox">
					<IconButton
						aria-label={`${open ? 'Hide' : 'Show'} the fulfillment chain for ${name}`}
						aria-expanded={open}
						aria-controls={chainId}
						onClick={() => setOpen( ( prev ) => !prev )}
					>
						{open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
					</IconButton>
				</TableCell>
				<TableCell sx={NO_WRAP}>
					<Link
						to={`/dc-admin/pick-orders/${pickOrder.id}`}
						onClick={() => heapEvent( 'Inventory - DC Admin - Pick Orders - List - Click - Row', { pick_order_id: pickOrder.id } )}
					>
						{name}
					</Link>
				</TableCell>
				<TableCell>
					<Chip
						label={PICK_ORDER_STATUS_LABEL[ pickOrder.status ] ?? pickOrder.status}
						color={PICK_ORDER_STATUS_CHIP_COLOR[ pickOrder.status ] ?? 'default'}
					/>
				</TableCell>
				<TableCell sx={NO_WRAP}>{formatDate( earliestNeededBy( pickOrder.fulfillmentOrders ) ) ?? '—'}</TableCell>
				<TableCell><Destination pickOrder={pickOrder} /></TableCell>
				<TableCell sx={[ WIDE_ONLY, NO_WRAP ]}>{runSize( pickOrder.lines )}</TableCell>
				<TableCell sx={WIDE_ONLY}>{userDisplayName( pickOrder.assignedToUser ) ?? 'Unassigned'}</TableCell>
				<TableCell sx={[ WIDE_ONLY, NO_WRAP ]}>{formatDateTime( pickOrder.dateCreated )}</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={COLUMN_COUNT} sx={{ py: 0, ...( open ? {} : { borderBottom: 0 } ) }}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box id={chainId} sx={{ py: 1.5, pl: { md: 6 } }}>
							<FulfillmentChainTable chain={fulfillmentChain( pickOrder )} label={`Fulfillment chain for ${name}`} />
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	)
}

export default function PickOrders(): ReactElement {
	const { selectedDcId, loading: dcLoading } = useDcAdminContext()

	const { data, loading, error } = usePickOrdersQuery( {
		variables: { fulfillingInventoryIds: selectedDcId ? [ selectedDcId ] : undefined },
		skip: !selectedDcId,
		fetchPolicy: 'cache-and-network',
	} )

	const pickOrders = data?.pickOrders.edges.map( ( edge ) => edge.node ) ?? []

	return (
		<ThemeProvider theme={pickFlowTheme}>
			<Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
				<Typography variant="h4" sx={{ mb: 2.5 }}>Pick Orders</Typography>

				{( dcLoading || loading ) && (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
						<CircularProgress />
					</Box>
				)}

				{error && <Alert severity="error">{error.message}</Alert>}

				{!loading && !error && pickOrders.length === 0 && (
					<Alert severity="info">
						No pick orders at this DC yet. Start one from a fulfillment order.
					</Alert>
				)}

				{pickOrders.length > 0 && (
					<TableContainer component={Paper} variant="outlined" sx={{ borderColor: 'pickFlow.cardBorder' }}>
						<Table>
							<TableHead>
								<TableRow sx={{ 'bgcolor': 'grey.50', '& > th': NO_WRAP }}>
									<TableHeaderCell padding="checkbox"><Box component="span" sx={VISUALLY_HIDDEN}>Expand</Box></TableHeaderCell>
									<TableHeaderCell>Pick Order</TableHeaderCell>
									<TableHeaderCell>Status</TableHeaderCell>
									<TableHeaderCell>Needed By</TableHeaderCell>
									<TableHeaderCell>Destination</TableHeaderCell>
									<TableHeaderCell sx={WIDE_ONLY}>Size</TableHeaderCell>
									<TableHeaderCell sx={WIDE_ONLY}>Assignee</TableHeaderCell>
									<TableHeaderCell sx={WIDE_ONLY}>Created</TableHeaderCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{pickOrders.map( ( pickOrder ) => <PickOrderRow key={pickOrder.id} pickOrder={pickOrder} /> )}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Box>
		</ThemeProvider>
	)
}
